/**
 * Copyright 2018 Phenix Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
define([
    'phenix-web-lodash-light',
    'phenix-web-assert',
    'phenix-web-event',
    'phenix-web-reconnecting-websocket',
    './MQService'
], function(_, assert, event, ReconnectingWebSocket, MQService) {
    'use strict';

    function MQWebSocket(uri, logger, protocols, apiVersion) {
        assert.isStringNotEmpty(uri, 'uri');
        assert.isObject(logger, 'logger');
        assert.isArray(protocols, 'protocols');

        // TODO (DY) Don't dispose of event listeners on disconnect event
        this._uri = uri;
        this._logger = logger;
        this._logger.info('Connecting to [%s]', uri);

        this._webSocket = new ReconnectingWebSocket(this._uri, this._logger);
        this._mqService = new MQService(this._logger, _.bind(sendMessage, this), _.bind(setOnMessage, this), protocols, apiVersion);
        this._namedEvents = new event.NamedEvents();

        this._webSocket.onconnected = _.bind(onConnected, this);
        this._webSocket.onreconnecting = _.bind(onReconnecting, this);
        this._webSocket.onreconnected = _.bind(onReconnected, this);
        this._webSocket.ondisconnected = _.bind(onDisconnected, this);
        this._webSocket.onerror = _.bind(onError, this);

        this._disconnected = false;
    }

    MQWebSocket.prototype.onEvent = function(eventName, handler) {
        this._namedEvents.listen(eventName, handler);

        return this._mqService.onEvent(eventName, handler);
    };

    MQWebSocket.prototype.onRequest = function(requestName, handler) {
        return this._mqService.onRequest(requestName, handler);
    };

    MQWebSocket.prototype.sendRequest = function sendRequest(type, message, callback, settings) {
        return this._mqService.sendRequest(type, message, callback, settings);
    };

    MQWebSocket.prototype.sendResponse = function sendResponse(requestId, type, message, callback) {
        return this._mqService.sendResponse(requestId, type, message, callback);
    };

    MQWebSocket.prototype.disconnect = function() {
        this._disconnected = true;

        return this._webSocket.disconnect();
    };

    MQWebSocket.prototype.disposeOfPendingRequests = function disposeOfPendingRequests() {
        this._logger.info('Disposing of pending MQ WebSocket requests');

        return this._mqService.disposeOfRequests();
    };

    MQWebSocket.prototype.getApiVersion = function getApiVersion() {
        return this._mqService.getApiVersion();
    };

    MQWebSocket.prototype.toString = function() {
        return 'MQWebSocket[' + this._webSocket.toString() + ']';
    };

    function triggerEvent(eventName, args) {
        this._namedEvents.fire(eventName, args, this);
    }

    function onReconnecting(evt) { // eslint-disable-line no-unused-vars
        triggerEvent.call(this, 'reconnecting');
    }

    function onConnected(evt) { // eslint-disable-line no-unused-vars
        triggerEvent.call(this, 'connected');
    }

    function onReconnected(evt) { // eslint-disable-line no-unused-vars
        triggerEvent.call(this, 'reconnected');
    }

    function onDisconnected(evt) {
        triggerEvent.call(this, 'disconnected', [evt.code, evt.reason]);

        if (this._disconnected) {
            this._namedEvents.dispose();
            this._mqService.dispose();
        }
    }

    function onError(evt) {
        triggerEvent.call(this, 'error', [evt.data]);
    }

    function setOnMessage(callback) {
        this._webSocket.onmessage = callback;
    }

    function sendMessage(message) {
        if (this._webSocket) {
            this._webSocket.send(message);
        }
    }

    return MQWebSocket;
});